import { SHELL_HELP_TEXT, SHELL_HELP_TEXT_NO_TOKEN_CREATE } from './helpText';
import {
  formatBalances,
  formatContacts,
  formatInbox,
  formatMerchants,
  formatMoney,
  formatPaymentMethods,
  formatTransactions,
} from './formatters';
import { parseCommand } from './parseCommand';
import type {
  ShellApi,
  ShellExecuteResult,
  ShellExecutor,
  ShellIntent,
  ShellScriptResult,
} from './types';

function filterTransactionsByDays<T extends { createdAt?: string }>(
  items: T[],
  days?: number
): T[] {
  if (!days) return items;
  const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
  return items.filter((tx) => {
    if (!tx.createdAt) return true;
    return new Date(tx.createdAt).getTime() >= cutoff;
  });
}

async function executeIntent(
  api: ShellApi,
  intent: ShellIntent,
  defaultCurrency: string
): Promise<ShellExecuteResult> {
  const canCreateTokens = typeof api.createToken === 'function';

  switch (intent.type) {
    case 'help':
      return { message: canCreateTokens ? SHELL_HELP_TEXT : SHELL_HELP_TEXT_NO_TOKEN_CREATE };

    case 'balances': {
      const profile = await api.getProfile();
      const lines = formatBalances(profile.balances, intent.currency);
      return {
        message: intent.currency
          ? `Your ${intent.currency} balance:\n${lines}`
          : `Your balances:\n${lines}`,
        action: 'show-balances',
        data: { balances: profile.balances, currency: intent.currency },
      };
    }

    case 'profile': {
      const p = await api.getProfile();
      const name = [p.firstName, p.lastName].filter(Boolean).join(' ') || '—';
      return {
        message:
          `👤 Profile\n` +
          `Name: ${name}\n` +
          `Username: ${p.username ?? '—'}\n` +
          `Email: ${p.email ?? '—'}\n` +
          `Phone: ${p.telephone ?? '—'}\n` +
          `Default currency: ${p.currency ?? defaultCurrency}`,
        data: { profile: p },
      };
    }

    case 'create-token': {
      if (!api.createToken) {
        return {
          message:
            'Token generation is not available in this app. Create cash tokens and invoices on vaiu.app.',
        };
      }

      const profile = await api.getProfile();
      const balance = profile.balances?.[intent.currency] ?? 0;
      const needsFunding = balance < intent.amount && !intent.fundAsNeeded;

      if (intent.isInvoice) {
        const params = new URLSearchParams({
          amount: String(intent.amount),
          currency: intent.currency,
          type: 'invoice',
        });
        if (intent.message) params.set('message', intent.message);
        return {
          message: `Invoice creation needs the full UI for line items and delivery options. Opening create flow for ${formatMoney(intent.amount, intent.currency)}…`,
          action: 'create-token-ui',
          data: {
            path: `/?${params.toString()}`,
            amount: intent.amount,
            currency: intent.currency,
            isInvoice: true,
          },
        };
      }

      if (needsFunding) {
        const params = new URLSearchParams({
          tokenAmount: String(intent.amount),
          tokenCurrency: intent.currency,
        });
        if (intent.message) params.set('tokenMessage', intent.message);
        return {
          message:
            `Insufficient ${intent.currency} balance (${formatMoney(balance, intent.currency)}). ` +
            `Opening token creation with top-up for ${formatMoney(intent.amount, intent.currency)}…`,
          action: 'create-token-ui',
          data: {
            path: `/?${params.toString()}`,
            amount: intent.amount,
            currency: intent.currency,
          },
        };
      }

      const result = await api.createToken({
        amount: intent.amount,
        currency: intent.currency,
        message: intent.message,
        fundAsNeeded: intent.fundAsNeeded,
      });

      let msg =
        `✅ Token created!\n` +
        `Key: ${result.key}\n` +
        `Amount: ${formatMoney(result.amount, result.currency)}\n` +
        `Status: ${result.status ?? 'active'}`;
      if (result.expiresAt) {
        msg += `\nExpires: ${new Date(result.expiresAt).toLocaleString()}`;
      }
      if (result.recipientDelivery?.message) {
        msg += `\n${result.recipientDelivery.message}`;
      }
      return {
        message: msg,
        data: { tokenKey: result.key, ...result },
      };
    }

    case 'redeem': {
      const result = await api.redeemToken(intent.tokenCode, intent.amount);
      const ok = result.success !== false;
      const msg = ok
        ? `✅ Redeemed ${intent.tokenCode}` +
          (result.amount != null && result.currency
            ? ` — ${formatMoney(result.amount, result.currency)}`
            : '') +
          (result.newBalance != null && result.currency
            ? `\nNew balance: ${formatMoney(result.newBalance, result.currency)}`
            : '') +
          (result.transactionId ? `\nTransaction: ${result.transactionId}` : '')
        : `❌ Could not redeem ${intent.tokenCode}: ${result.message ?? 'Unknown error'}`;

      if (!ok && result.message?.toLowerCase().includes('invoice')) {
        return {
          message: msg + '\n\nThis may be an invoice token — opening redeem UI…',
          action: 'redeem-token-ui',
          data: { path: `/redeem/${intent.tokenCode}`, tokenCode: intent.tokenCode },
        };
      }

      return { message: msg, data: { ...result, tokenCode: intent.tokenCode } };
    }

    case 'token-info': {
      const details = await api.getTokenDetails(intent.tokenCode);
      const token = (details.token ?? details) as Record<string, unknown>;
      const amount = token.amount as number | undefined;
      const currency = token.currency as string | undefined;
      return {
        message:
          `🪙 Token ${intent.tokenCode}\n` +
          `Type: ${String(token.type ?? '—')}\n` +
          `Status: ${String(token.status ?? '—')}\n` +
          `Amount: ${amount != null && currency ? formatMoney(amount, currency) : '—'}\n` +
          (token.message ? `Message: ${String(token.message)}\n` : '') +
          (token.expiresAt
            ? `Expires: ${new Date(String(token.expiresAt)).toLocaleString()}`
            : ''),
        data: details,
      };
    }

    case 'transactions': {
      const result = await api.getTransactions({
        completedLimit: intent.limit ?? 20,
        pendingLimit: 5,
      });
      const completed = filterTransactionsByDays(
        result.completed ?? result.transactions ?? [],
        intent.days
      );
      const pending = result.pending ?? [];
      const parts: string[] = [];
      if (pending.length) {
        parts.push(`⏳ Pending (${pending.length}):\n${formatTransactions(pending, 5)}`);
      }
      parts.push(
        `📜 Recent${intent.days ? ` (last ${intent.days} days)` : ''}:\n` +
          formatTransactions(completed, 10)
      );
      return {
        message: parts.join('\n\n'),
        data: { pending, completed },
      };
    }

    case 'contacts': {
      const contacts = intent.search
        ? await api.searchContacts(intent.search)
        : await api.getContacts();
      const header = intent.search
        ? `Contacts matching "${intent.search}":`
        : 'Your contacts:';
      return {
        message: `${header}\n${formatContacts(contacts)}`,
        data: { contacts },
      };
    }

    case 'payment-methods': {
      const methods = await api.getPaymentMethods();
      return {
        message: `Payment methods:\n${formatPaymentMethods(methods)}`,
        data: { methods },
      };
    }

    case 'inbox': {
      const messages = await api.getInboxMessages({
        unreadOnly: intent.unreadOnly,
        limit: 15,
      });
      const header = intent.unreadOnly ? 'Unread inbox:' : 'Inbox:';
      return {
        message: `${header}\n${formatInbox(messages)}`,
        data: { messages },
      };
    }

    case 'referrals': {
      const dashboard = await api.getReferralDashboard();
      const stats = dashboard as Record<string, unknown>;
      return {
        message:
          `🎁 Referrals\n` +
          `Tokens created: ${String(stats.tokensCreated ?? stats.totalTokens ?? '—')}\n` +
          `Rewards earned: ${String(stats.totalRewards ?? stats.rewardsEarned ?? '—')}\n` +
          `Active referrals: ${String(stats.activeReferrals ?? '—')}\n\n` +
          `Say "open referral tokens" for the full dashboard.`,
        data: dashboard,
      };
    }

    case 'subscriptions': {
      const subs = await api.getSubscriptions();
      if (!subs.length) {
        return { message: 'You have no merchant subscriptions.' };
      }
      const list = subs
        .map((s, i) => `${i + 1}. ${s.merchantName} (${s.merchantId})`)
        .join('\n');
      return { message: `Subscribed merchants:\n${list}`, data: { subscriptions: subs } };
    }

    case 'search-merchants': {
      const merchants = await api.searchMerchants(intent.query);
      return {
        message: `Merchants matching "${intent.query}":\n${formatMerchants(merchants)}`,
        data: { merchants, query: intent.query },
      };
    }

    case 'search-marketplace': {
      const q = intent.query ?? '';
      const path = q
        ? `/marketplace?search=${encodeURIComponent(q)}`
        : '/marketplace';
      return {
        message: q
          ? `Opening marketplace search for "${q}"…`
          : 'Opening marketplace…',
        action: 'navigate',
        data: { path },
      };
    }

    case 'navigate':
      return {
        message: `Opening ${intent.label}…`,
        action: 'navigate',
        data: { path: intent.path, label: intent.label },
      };

    case 'topup': {
      const params = new URLSearchParams();
      if (intent.amount) params.set('amount', String(intent.amount));
      if (intent.currency) params.set('currency', intent.currency);
      const qs = params.toString();
      return {
        message: intent.amount
          ? `Opening top-up for ${formatMoney(intent.amount, intent.currency ?? defaultCurrency)}…`
          : 'Opening top-up…',
        action: 'navigate',
        data: { path: qs ? `/topup?${qs}` : '/topup' },
      };
    }

    case 'transfer-out': {
      const currency = intent.currency ?? defaultCurrency;
      return {
        message: `Opening transfer-out for ${currency}…`,
        action: 'navigate',
        data: { path: `/transfer-out/${currency}` },
      };
    }

    case 'transfer': {
      const params = new URLSearchParams();
      if (intent.amount) params.set('amount', String(intent.amount));
      if (intent.currency) params.set('currency', intent.currency);
      if (intent.recipient) params.set('recipient', intent.recipient);
      const qs = params.toString();
      const summary = intent.amount
        ? formatMoney(intent.amount, intent.currency ?? defaultCurrency)
        : 'funds';
      const to = intent.recipient ? ` to ${intent.recipient}` : '';
      return {
        message: `Opening transfer UI for ${summary}${to}…`,
        action: 'navigate',
        data: { path: qs ? `/transfer?${qs}` : '/transfer' },
      };
    }

    case 'support-tickets':
      return {
        message: 'Opening support…',
        action: 'navigate',
        data: { path: '/support' },
      };

    case 'unknown':
      return {
        message:
          `I'm not sure what you mean by "${intent.raw}".\n\n` +
          `Try "help" for commands, or:\n` +
          `• "show balances"\n` +
          (canCreateTokens ? `• "create $50 token"\n` : '') +
          `• "redeem ABC123"\n` +
          `• "open transactions"`,
      };
  }
}

export interface CreateShellExecutorOptions {
  api: ShellApi;
  /** User default currency from profile/auth context */
  getDefaultCurrency?: () => string;
}

export function createShellExecutor(
  options: CreateShellExecutorOptions
): ShellExecutor {
  const { api, getDefaultCurrency } = options;

  const execute = async (command: string): Promise<ShellExecuteResult> => {
    const start = performance.now();
    const defaultCurrency = getDefaultCurrency?.() ?? 'USD';
    const intent = parseCommand(command, defaultCurrency);

    try {
      const result = await executeIntent(api, intent, defaultCurrency);
      return {
        ...result,
        executionTime: Math.round(performance.now() - start),
      };
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Something went wrong.';
      return {
        message: `❌ ${message}`,
        executionTime: Math.round(performance.now() - start),
      };
    }
  };

  const executeScript = async (scriptContent: string): Promise<ShellScriptResult> => {
    const lines = scriptContent
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line && !line.startsWith('#'));

    const results: ShellScriptResult['results'] = [];
    let successCount = 0;
    let failureCount = 0;

    for (let i = 0; i < lines.length; i++) {
      const command = lines[i];
      const lineNumber = i + 1;
      try {
        const result = await execute(command);
        const failed = result.message.startsWith('❌');
        if (failed) failureCount++;
        else successCount++;
        results.push({
          lineNumber,
          command,
          success: !failed,
          result,
          error: failed ? result.message : undefined,
        });
      } catch (error) {
        failureCount++;
        results.push({
          lineNumber,
          command,
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        });
      }
    }

    return {
      totalCommands: lines.length,
      successCount,
      failureCount,
      results,
    };
  };

  return { execute, executeScript };
}

export { parseCommand, COMMAND_TEMPLATES } from './parseCommand';
